import { forwardRef, type ComponentProps } from 'react';

import { cn } from '@/lib/utils';

const inputBaseStyles = [
  'h-12 w-full min-w-0 rounded-1.5xl border px-4 py-3.5 text-base',
  'transition-colors outline-none',
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  'placeholder:text-gray-400',
  // Default
  'bg-white border-gray-200 text-indigo-900',
  // Error (palette: red-25 bg, red-300 border)
  'aria-invalid:bg-red-25 aria-invalid:border-red-300',
  // Focus (indigo; !important so it overrides error state when focused)
  'focus:!border-indigo-900 focus:outline-none',
].join(' ');

const inputHasValueStyles =
  '!bg-blue-50 font-medium focus:!bg-white focus:!border-indigo-900';

function Input({ className, type, value, ...props }: ComponentProps<'input'>) {
  const hasValue =
    value !== undefined && value !== null && String(value).trim() !== '';

  return (
    <input
      type={type}
      value={value}
      data-slot="input"
      className={cn(
        inputBaseStyles,
        hasValue && inputHasValueStyles,
        className,
      )}
      {...props}
    />
  );
}

const textareaBaseStyles = [
  'min-h-40 w-full min-w-0 rounded-1.5xl border px-4 py-3.5 text-base resize-none',
  'transition-colors outline-none',
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  'placeholder:text-gray-500',
  'bg-white border-gray-200 text-indigo-900',
  'aria-invalid:bg-red-25 aria-invalid:border-red-300',
  'focus:!border-indigo-900 focus:outline-none',
].join(' ');

const textareaHasValueStyles =
  '!bg-blue-50 font-medium focus:!bg-white focus:!border-indigo-900';

const Textarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<'textarea'> & { value?: string }
>(function Textarea({ className, value, ...props }, ref) {
  const hasValue =
    value !== undefined && value !== null && String(value).trim() !== '';

  return (
    <textarea
      ref={ref}
      value={value}
      data-slot="textarea"
      className={cn(
        textareaBaseStyles,
        hasValue && textareaHasValueStyles,
        className,
      )}
      {...props}
    />
  );
});

export { Input, Textarea };
